import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CatalogListCardRecord, useCatalogApi, VersionedImageReview } from './catalog-api';

const REVIEW_LIFETIME_MS = 300000;
const MAX_CLOCK_SKEW_MS = 5000;

/** No persisted receipt and no latest-image fallback for an enrolled card. */
export function useVersionedImageReview<T extends CatalogListCardRecord>(
  card: T | null,
  catalog: ReturnType<typeof useCatalogApi>,
  purpose: 'FINAL' | 'PRELIMINARY',
) {
  const cardId = card?.cardId;
  const required = card?.versionedImageReviewRequired === true;
  const [generation, setGeneration] = useState(0);
  const [review, setReview] = useState<{
    receipt: VersionedImageReview;
    context: string;
    displayDeadline: number;
  } | null>(null);
  // Invalidate immediately on a refreshed image state, before the next effect runs.
  const source = card as Record<string, unknown> | null;
  const context = JSON.stringify([
    cardId,
    purpose,
    generation,
    required,
    ...[
      'images',
      'imageRef',
      'imageQualityStatus',
      'imageQualityReviewedAt',
      'customerPhotoRevision',
      'customerPhotoLifecycleId',
      'customerApprovedPhotoSources',
      'pendingImageReplacement',
      'imageReplacedAt',
      'preliminaryImageReview',
    ].map((field) => {
      const value = source?.[field];
      // Signed display URLs can refresh without changing the authoritative replacement.
      if (field === 'pendingImageReplacement' && value && typeof value === 'object') {
        const { imageUrls: _urls, ...state } = value as Record<string, unknown>;
        return state;
      }
      return value;
    }),
  ]);
  const [error, setError] = useState<string | null>(null);
  const epoch = useRef(0);
  const reload = useCallback(() => {
    epoch.current++;
    setReview(null);
    setGeneration((n) => n + 1);
  }, []);
  useEffect(() => {
    const requestEpoch = ++epoch.current;
    setReview(null);
    setError(null);
    if (!required || !cardId) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const images: HTMLImageElement[] = [];
    let expired = false;
    const current = () => requestEpoch === epoch.current && !expired;
    void (async () => {
      try {
        const result = await catalog.beginVersionedImageReview(cardId, purpose);
        if (!current()) return;
        if (!result.ok) throw new Error('Images could not be prepared.');
        const next = result.data;
        const receivedAt = Date.now();
        const receivedMonotonic = performance.now();
        const urls = Object.values(next.imageUrls);
        if (
          next.cardId !== cardId ||
          !next.reviewId ||
          !urls.length ||
          urls.length > 2 ||
          !Number.isSafeInteger(next.expiresAt) ||
          next.expiresAt <= receivedAt ||
          next.expiresAt > receivedAt + REVIEW_LIFETIME_MS + MAX_CLOCK_SKEW_MS
        )
          throw new Error('Image review expired.');
        // Server expiry stays authoritative. Allow bounded clock skew at admission,
        // but shorten local use by the full skew bound, never extending the receipt.
        const lifetime =
          Math.min(REVIEW_LIFETIME_MS, next.expiresAt - receivedAt) - MAX_CLOCK_SKEW_MS;
        if (lifetime <= 0) throw new Error('Image review expired.');
        const displayDeadline = receivedMonotonic + lifetime;
        timer = setTimeout(() => {
          if (current()) {
            expired = true;
            setReview(null);
            setError('Image review expired.');
          }
        }, lifetime);
        await Promise.all(
          urls.map(
            (url) =>
              new Promise<void>((resolve, reject) => {
                const image = new Image();
                images.push(image);
                image.onload = () => (image.naturalWidth > 0 ? resolve() : reject(new Error()));
                image.onerror = () => reject(new Error());
                image.src = url;
              }),
          ),
        );
        if (!current()) return;
        if (performance.now() >= displayDeadline) throw new Error('Image review expired.');
        setReview({ receipt: next, context, displayDeadline });
      } catch {
        if (current()) {
          setReview(null);
          setError('Images unavailable or changed. Reload to review.');
        }
      }
    })();
    return () => {
      if (requestEpoch === epoch.current) epoch.current++;
      if (timer) clearTimeout(timer);
      for (const image of images) {
        image.onload = null;
        image.onerror = null;
        image.src = '';
      }
    };
  }, [cardId, required, catalog, purpose, context]);
  const valid =
    review !== null && review.context === context && review.displayDeadline > performance.now()
      ? review.receipt
      : null;
  const displayed = useMemo(
    () =>
      card === null || !required
        ? card
        : { ...card, imageUrl: undefined, imageUrls: valid?.imageUrls ?? {} },
    [card, required, valid],
  );
  return {
    card: displayed,
    required,
    ready: !required || valid !== null,
    reviewId: valid?.reviewId,
    error,
    reload,
  };
}

export function VersionedImageReviewStatus({
  required,
  ready,
  error,
  reload,
}: Pick<ReturnType<typeof useVersionedImageReview>, 'required' | 'ready' | 'error' | 'reload'>) {
  if (!required) return null;
  return (
    <div role="status" className={error ? 'tcvs-error' : 'tcvs-muted'}>
      {error ?? (ready ? 'Images ready for review.' : 'Loading review images...')}{' '}
      <button type="button" className="tcvs-link-button" onClick={reload}>
        Reload images
      </button>
    </div>
  );
}
